/**
 * LiveKit data-channel command bus.
 *
 * Encodes the AGENTS.md §9 envelope and exposes a small surface for:
 *   - sending a command to a specific participant + awaiting its ack
 *   - registering a handler for a topic/type that returns the ack payload
 *
 * Reliable channel only (the unreliable channel is reserved for high-
 * frequency cursor/pointer updates, none in 1B-B). Acks are correlated by
 * the original message `id` and a fresh `id` on the response.
 *
 * The implementation is deliberately framework-light: it accepts an object
 * conforming to a small `Transport` interface so unit tests can stub it
 * without booting a real LiveKit Room.
 */
import type { Room } from "livekit-client";
import { DataPacket_Kind } from "livekit-client";

export type EnvelopeIdentity =
  | { kind: "user"; id: string }
  | { kind: "participant"; id: string }
  | { kind: "service"; id: string };

export type Envelope<P = unknown> = {
  v: 1;
  id: string;
  ts: number;
  session: string;
  from: EnvelopeIdentity;
  to?: EnvelopeIdentity;
  topic: "command" | "annotation" | "presence" | "step" | "form" | "system" | "evidence" | "chat" | "whiteboard";
  type: string;
  payload: P;
  requires_ack?: boolean;
  /** Set on responses; correlates back to the originating message. */
  in_reply_to?: string;
  /** True for an explicit nack with a reason in payload.error. */
  is_nack?: boolean;
};

export type Transport = {
  /** Publish an envelope (already JSON-stringified). `reliable` defaults
   *  to true; set to false for high-frequency drops-allowed traffic
   *  (live draft strokes, pointer/cursor — AGENTS.md §9). */
  publish: (
    bytes: Uint8Array,
    opts?: { destinationIdentities?: string[]; reliable?: boolean },
  ) => Promise<void>;
  /** Register a callback for inbound envelopes. Returns an unsubscribe fn. */
  onMessage: (cb: (env: Envelope) => void) => () => void;
};

export type Handler<P = unknown, R = unknown> = (env: Envelope<P>) => Promise<R> | R;

const TEXT_DECODER = new TextDecoder();
const TEXT_ENCODER = new TextEncoder();


export function makeLiveKitTransport(room: Room, sessionId: string): Transport {
  return {
    publish: async (bytes, opts) => {
      await room.localParticipant.publishData(bytes, {
        reliable: opts?.reliable !== false,
        destinationIdentities: opts?.destinationIdentities,
      });
    },
    onMessage: (cb) => {
      const handler = (payload: Uint8Array) => {
        try {
          const env = JSON.parse(TEXT_DECODER.decode(payload)) as Envelope;
          if (env?.v === 1 && env.session === sessionId) cb(env);
        } catch {
          /* malformed packets are silently dropped — same connection serves
             other features that may publish their own bytes */
        }
      };
      room.on("dataReceived", handler);
      return () => {
        room.off("dataReceived", handler);
      };
    },
  };
  // DataPacket_Kind import keeps the bundle tree-shaker happy; not used here
  // but the type is part of the public livekit-client surface and ensures
  // we get a build-time error if the SDK shifts the API.
  void DataPacket_Kind;
}


export type SendOpts = {
  to?: EnvelopeIdentity;
  to_identities?: string[];
  /** ms; defaults to 30s. Long enough for a field-side capture+upload roundtrip. */
  timeout_ms?: number;
  /** Default true. Set to false for high-frequency drops-allowed traffic
   *  (live draft strokes — AGENTS.md §9). Only applies to `send`; `request`
   *  always uses reliable so the ack can correlate. */
  reliable?: boolean;
};

export type AuditMirror = (envelope: Envelope) => Promise<void> | void;


export class CommandBus {
  private _pending = new Map<string, (env: Envelope) => void>();
  // Multiple subscribers per topic/type are allowed: the live-overlay sync
  // and the EvidenceAnnotator both listen on `annotation/draw.create` and
  // filter envelopes by `payload.annotation.evidence`. For envelopes that
  // require an ack (request/response flows), only the FIRST handler's
  // result becomes the ack — additional handlers fire as side effects.
  private _handlers = new Map<string, Handler[]>();
  private _unsub: () => void;
  private _auditMirror: AuditMirror | null = null;

  constructor(
    private readonly transport: Transport,
    private readonly sessionId: string,
    private readonly self: EnvelopeIdentity,
  ) {
    this._unsub = transport.onMessage((env) => this._dispatch(env));
  }

  /** Register a callback that fires for every successfully-published
   *  outbound envelope. Used by Phase 1B-D to mirror `command/*`
   *  envelopes to the backend audit log — so camera switches, torch
   *  toggles, etc. hash into the chain. Replacing an existing mirror is
   *  fine; only one is active at a time. Pass `null` to unregister. */
  setAuditMirror(mirror: AuditMirror | null): void {
    this._auditMirror = mirror;
  }

  dispose(): void {
    this._unsub();
    this._pending.clear();
    this._handlers.clear();
  }

  /** Register a handler for `topic/type` (e.g. "command/capture.snapshot").
   *  Multiple handlers for the same key are stacked in registration order. */
  on<P = unknown, R = unknown>(key: string, handler: Handler<P, R>): () => void {
    const existing = this._handlers.get(key) ?? [];
    existing.push(handler as Handler);
    this._handlers.set(key, existing);
    return () => {
      const arr = this._handlers.get(key);
      if (!arr) return;
      const idx = arr.indexOf(handler as Handler);
      if (idx >= 0) arr.splice(idx, 1);
      if (arr.length === 0) this._handlers.delete(key);
    };
  }

  /** Send a fire-and-forget envelope (no ack tracking). */
  async send<P>(input: {
    topic: Envelope["topic"];
    type: string;
    payload: P;
    requires_ack?: boolean;
  } & SendOpts): Promise<void> {
    const env = this._build(input);
    await this._publish(env, input.to_identities, input.reliable);
  }

  /**
   * Send and await an ack envelope correlated by `id`. Resolves with the
   * ack's payload, or rejects on timeout / nack.
   */
  async request<P, R>(input: {
    topic: Envelope["topic"];
    type: string;
    payload: P;
  } & SendOpts): Promise<R> {
    const env = this._build({ ...input, requires_ack: true });
    const timeout = input.timeout_ms ?? 30_000;

    return new Promise<R>((resolve, reject) => {
      const timer = setTimeout(() => {
        this._pending.delete(env.id);
        reject(new Error(`command ${env.type} timed out after ${timeout}ms`));
      }, timeout);

      this._pending.set(env.id, (reply) => {
        clearTimeout(timer);
        this._pending.delete(env.id);
        if (reply.is_nack) {
          reject(new Error(String((reply.payload as { error?: string })?.error ?? "nack")));
        } else {
          resolve(reply.payload as R);
        }
      });

      this._publish(env, input.to_identities).catch((e) => {
        clearTimeout(timer);
        this._pending.delete(env.id);
        reject(e);
      });
    });
  }

  // ---------- internals ----------

  private _build<P>(input: {
    topic: Envelope["topic"];
    type: string;
    payload: P;
    requires_ack?: boolean;
    to?: EnvelopeIdentity;
    in_reply_to?: string;
    is_nack?: boolean;
  }): Envelope<P> {
    const env: Envelope<P> = {
      v: 1,
      id: `msg_${cryptoRandomId()}`,
      ts: Date.now(),
      session: this.sessionId,
      from: this.self,
      topic: input.topic,
      type: input.type,
      payload: input.payload,
    };
    if (input.to) env.to = input.to;
    if (input.requires_ack) env.requires_ack = true;
    if (input.in_reply_to) env.in_reply_to = input.in_reply_to;
    if (input.is_nack) env.is_nack = true;
    return env;
  }

  private async _publish(
    env: Envelope,
    identities?: string[],
    reliable?: boolean,
  ): Promise<void> {
    const bytes = TEXT_ENCODER.encode(JSON.stringify(env));
    const opts: { destinationIdentities?: string[]; reliable?: boolean } = {};
    if (identities) opts.destinationIdentities = identities;
    if (reliable !== undefined) opts.reliable = reliable;
    await this.transport.publish(bytes, opts);
    // Mirror command envelopes to the backend audit log AFTER the bus
    // publish succeeds. Best-effort: a mirror failure must NOT prevent
    // the user-facing command from working (the LiveKit publish is the
    // load-bearing path). Other topics already have dedicated audit
    // hooks at their server-side write sites — log here would double-emit.
    if (env.topic === "command" && this._auditMirror) {
      try {
        await this._auditMirror(env);
      } catch {
        /* swallow — audit hooks on the receiving side will catch acked
           commands that landed server-side anyway */
      }
    }
  }

  private async _dispatch(env: Envelope): Promise<void> {
    // Drop envelopes from a different session — defence in depth on top of
    // the transport's session filter, so any Transport implementation is
    // safe by default.
    if (!env || env.v !== 1 || env.session !== this.sessionId) return;
    // Acks come back addressed to the requester; correlate by in_reply_to.
    if (env.in_reply_to && this._pending.has(env.in_reply_to)) {
      this._pending.get(env.in_reply_to)!(env);
      return;
    }
    // Inbound command: dispatch to every registered handler for this key.
    const key = `${env.topic}/${env.type}`;
    const handlers = this._handlers.get(key);
    if (!handlers || handlers.length === 0) return;

    if (!env.requires_ack) {
      for (const h of handlers) {
        try {
          void h(env);
        } catch {
          /* handler errors on fire-and-forget envelopes are swallowed —
             other subscribers still need to fire */
        }
      }
      return;
    }
    // Request/response: only the FIRST handler produces the ack so we
    // never publish two responses for one request. Side-effect handlers
    // (registered later) fire in parallel.
    const ackHandler = handlers[0]!;
    for (let i = 1; i < handlers.length; i++) {
      try {
        void handlers[i]!(env);
      } catch {
        /* swallow */
      }
    }
    try {
      const result = await ackHandler(env);
      const ack = this._build({
        topic: "system",
        type: "ack",
        payload: result,
        in_reply_to: env.id,
        to: env.from,
      });
      await this._publish(ack);
    } catch (err) {
      const nack = this._build({
        topic: "system",
        type: "ack",
        payload: { error: String((err as Error).message ?? err) },
        in_reply_to: env.id,
        is_nack: true,
        to: env.from,
      });
      await this._publish(nack);
    }
  }
}


function cryptoRandomId(): string {
  // Browsers expose crypto.randomUUID; fall back to Math.random in jsdom
  // without webcrypto polyfill (deterministic enough for a non-secret id).
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}